/** @format */

import { RootState } from "../store/store";

// ==================== Payout Overview Selectors ====================

/**
 * Select the complete payout overview data
 */
export const selectPayoutOverview = (state: RootState) =>
	state.adminPayout?.payoutOverview ?? null;

/**
 * Select payout overview summary statistics
 */
export const selectPayoutSummary = (state: RootState) =>
	state.adminPayout?.payoutOverview?.summary ?? null;

/**
 * Select past payouts (completed, missed, failed)
 */
export const selectPastPayouts = (state: RootState) =>
	state.adminPayout?.payoutOverview?.past ?? null;

/**
 * Select completed payouts only
 */
export const selectCompletedPayouts = (state: RootState) =>
	state.adminPayout?.payoutOverview?.past?.completed ?? [];

/**
 * Select missed payouts only
 */
export const selectMissedPayouts = (state: RootState) =>
	state.adminPayout?.payoutOverview?.past?.missed ?? [];

/**
 * Select failed payouts only
 */
export const selectFailedPayouts = (state: RootState) =>
	state.adminPayout?.payoutOverview?.past?.failed ?? [];

/**
 * Select today's payouts
 */
export const selectTodayPayouts = (state: RootState) =>
	state.adminPayout?.payoutOverview?.today ?? [];

/**
 * Select upcoming payouts
 */
export const selectUpcomingPayouts = (state: RootState) =>
	state.adminPayout?.payoutOverview?.upcoming ?? [];

/**
 * Select payouts grouped by circle
 */
export const selectPayoutsByCircle = (state: RootState) =>
	state.adminPayout?.payoutOverview?.byCircle ?? [];

// ==================== Retryable Payouts Selectors ====================

/**
 * Select all retryable payouts data
 */
export const selectRetryablePayouts = (state: RootState) =>
	state.adminPayout?.retryablePayouts ?? null;

/**
 * Select retryable payouts total count
 */
export const selectRetryablePayoutsCount = (state: RootState) =>
	state.adminPayout?.retryablePayouts?.total ?? 0;

/**
 * Select missed retryable payouts
 */
export const selectRetryableMissedPayouts = (state: RootState) =>
	state.adminPayout?.retryablePayouts?.missed ?? { count: 0, payouts: [] };

/**
 * Select failed retryable payouts
 */
export const selectRetryableFailedPayouts = (state: RootState) =>
	state.adminPayout?.retryablePayouts?.failed ?? { count: 0, payouts: [] };

/**
 * Select all retryable payouts list
 */
export const selectAllRetryablePayoutsList = (state: RootState) =>
	state.adminPayout?.retryablePayouts?.all ?? [];

// ==================== Filtered Payouts Selectors ====================

/**
 * Select payouts by circle
 */
export const selectPayoutsByCircleData = (state: RootState, circleId: string) =>
	state.adminPayout?.payoutsByCircle?.[circleId] ?? null;

/**
 * Select payouts by member
 */
export const selectPayoutsByMemberData = (state: RootState, memberId: string) =>
	state.adminPayout?.payoutsByMember?.[memberId] ?? null;

/**
 * Select payouts by cycle
 */
export const selectPayoutsByCycleData = (state: RootState, cycleId: string) =>
	state.adminPayout?.payoutsByCycle?.[cycleId] ?? null;

// ==================== Circle Payout Details Selectors ====================

/**
 * Select circle payout details by circleId
 */
export const selectCirclePayoutDetails = (state: RootState, circleId: string) =>
	state.adminPayout?.circlePayoutDetails?.[circleId] ?? null;

/**
 * Select circle payout details loading state
 */
export const selectCirclePayoutDetailsLoading = (state: RootState) =>
	state.adminPayout?.isLoadingCircleDetails ?? false;

/**
 * Select circle payout details error
 */
export const selectCirclePayoutDetailsError = (state: RootState) =>
	state.adminPayout?.circleDetailsError ?? null;

/**
 * Select next payout for a circle
 */
export const selectCircleNextPayout = (state: RootState, circleId: string) =>
	state.adminPayout?.circlePayoutDetails?.[circleId]?.nextPayout ?? null;

/**
 * Select payouts grouped by month for a circle
 */
export const selectCirclePayoutsByMonth = (
	state: RootState,
	circleId: string,
) => state.adminPayout?.circlePayoutDetails?.[circleId]?.payoutsByMonth ?? [];

/**
 * Select all payouts for a circle
 */
export const selectCircleAllPayouts = (state: RootState, circleId: string) =>
	state.adminPayout?.circlePayoutDetails?.[circleId]?.allPayouts ?? [];

/**
 * Select member summaries for a circle
 */
export const selectCircleMembers = (state: RootState, circleId: string) =>
	state.adminPayout?.circlePayoutDetails?.[circleId]?.members ?? [];

// ==================== Retry Results Selectors ====================

/**
 * Select the last retry result
 */
export const selectLastRetryResult = (state: RootState) =>
	state.adminPayout?.lastRetryResult ?? null;

/**
 * Select bulk retry result
 */
export const selectBulkRetryResult = (state: RootState) =>
	state.adminPayout?.bulkRetryResult ?? null;

/**
 * Select retry error
 */
export const selectRetryError = (state: RootState) =>
	state.adminPayout?.retryError ?? null;

// ==================== Loading & Error State Selectors ====================

/**
 * Select loading state for payout overview
 */
export const selectPayoutOverviewLoading = (state: RootState) =>
	state.adminPayout?.isLoadingOverview ?? false;

/**
 * Select loading state for retryable payouts
 */
export const selectRetryablePayoutsLoading = (state: RootState) =>
	state.adminPayout?.isLoadingRetryable ?? false;

/**
 * Select loading state for retry action
 */
export const selectRetryLoading = (state: RootState) =>
	state.adminPayout?.isRetrying ?? false;

/**
 * Select general loading state
 */
export const selectAdminPayoutLoading = (state: RootState) =>
	state.adminPayout?.isLoading ?? false;

/**
 * Select error state
 */
export const selectAdminPayoutError = (state: RootState) =>
	state.adminPayout?.error ?? null;

// ==================== Computed Selectors ====================

/**
 * Select total amount paid out (in dollars)
 */
export const selectTotalPaidOutDollars = (state: RootState) => {
	const cents =
		state.adminPayout?.payoutOverview?.summary?.totalPaidOutCents ?? 0;
	return cents / 100;
};

/**
 * Select completion rate as a number
 */
export const selectCompletionRateNumber = (state: RootState) => {
	const rate = state.adminPayout?.payoutOverview?.summary?.completionRate;
	if (!rate || rate === "N/A") return 0;
	return parseFloat(rate);
};

/**
 * Check if there are any retryable payouts
 */
export const selectHasRetryablePayouts = (state: RootState) =>
	(state.adminPayout?.retryablePayouts?.total ?? 0) > 0;

/**
 * Check if there are payouts due today
 */
export const selectHasPayoutsDueToday = (state: RootState) =>
	(state.adminPayout?.payoutOverview?.summary?.dueToday ?? 0) > 0;

/**
 * Select payouts that need attention (missed + failed + due today)
 */
export const selectPayoutsNeedingAttention = (state: RootState) => {
	const missed = state.adminPayout?.payoutOverview?.past?.missed ?? [];
	const failed = state.adminPayout?.payoutOverview?.past?.failed ?? [];
	const dueToday = state.adminPayout?.payoutOverview?.today ?? [];

	return {
		missed,
		failed,
		dueToday,
		totalCount: missed.length + failed.length + dueToday.length,
	};
};
